import { useCallback } from 'react'
import type { JSX } from 'react'

import clsx from 'clsx'

import {
  IconButton,
  Kbd,
  PlusIcon,
  Tooltip,
  TooltipGroup,
} from '@buffer-mono/popcorn'
import {
  BufferTrackerReact as BufferTracker,
  Product,
} from '@buffer-mono/tracking-plan'

import { useAccount, useOrganizationId } from '~publish/contexts/AccountContext'
import { getChannelNameForDisplay } from '~publish/helpers/getChannelNameForDisplay'
import { usePostComposer } from '~publish/hooks/usePostComposer'
import { canPerform } from '~publish/utils/permissions/canPerform'

import { ExpandableSidebarListItem } from '../ExpandableSidebarListItem'
import type { MainSidebarChannel } from './useChannels'

import styles from './ChannelItemActions.module.css'

type ChannelItemActionsProps = {
  channel: MainSidebarChannel
}

export function ChannelItemActions({
  channel,
}: ChannelItemActionsProps): JSX.Element {
  const { createNewPostInComposer } = usePostComposer()
  const organizationId = useOrganizationId()
  const { account } = useAccount()

  const commonTrackingProperties =
    account?.currentOrganization?.commonTrackingProperties ?? undefined

  const canPost = canPerform(channel, 'manageUpdates')

  const handleAddPost = useCallback((): void => {
    const cta = 'publish-mainSidebar-channelItem-addPost-1'

    const { ...trackingProps } = commonTrackingProperties || {}
    BufferTracker.globalPlusButtonItemClicked({
      organizationId: organizationId ?? '',
      product: Product.Publish,
      cta,
      clientName: 'publishWeb',
      upgradePathName: 'channelItem-addPost',
      ...trackingProps,
    })

    void createNewPostInComposer({
      cta,
      channels: [channel.id],
    })
  }, [
    channel.id,
    organizationId,
    commonTrackingProperties,
    createNewPostInComposer,
  ])

  const channelName = getChannelNameForDisplay(channel).trim() || 'Channel'

  return (
    <div className={styles.actions}>
      <TooltipGroup>
        {canPost && (
          <Tooltip
            content={
              <>
                New post <Kbd color="inverted">N + P</Kbd>
              </>
            }
          >
            <IconButton
              type="button"
              label="New post"
              variant="tertiary"
              size="small"
              onClick={handleAddPost}
              className={styles.actionButton}
            >
              <PlusIcon />
            </IconButton>
          </Tooltip>
        )}

        <ExpandableSidebarListItem.Toggle
          label={`${channelName} submenu`}
          className={clsx(styles.subNavToggle, styles.actionButton)}
        />
      </TooltipGroup>
    </div>
  )
}
